/**
 * Key-based data hook for ParkedDomainDrawer
 *
 * KEY-BASED PATTERN: Fetches parked domain data internally using parkedDomainKey,
 * eliminating race conditions from dual state sources. The URL param
 * (parkedDomainDrawerKey) is the single source of truth.
 *
 * This replaces the old pattern where:
 * 1. handleOpenParkedDomainDrawer() called setParkedDomain(parkedDomain) synchronously
 * 2. A useEffect in drawers/index.tsx called setParkedDomain() when API completed
 * 3. Another useEffect in useParkedDomainDrawer.ts called setParkedDomain()
 *
 * When clicking quickly between rows, stale API responses would overwrite newer state.
 * Now the drawer fetches its own data based on the URL param key.
 *
 * @param parkedDomainKey - The parked domain key to fetch data for (from URL param parkedDomainDrawerKey)
 * @returns Parked domain data, loading state, and refetch function
 */

import { useMy } from '@/hooks/useMy';
import { ParkedDomain, ResourceType } from '@/types';

export function useParkedDomainDrawerData(parkedDomainKey: string) {
  // Fetch parked domain data by key (single source of truth)
  const {
    data: parkedDomainData = [],
    status,
    refetch,
  } = useMy(
    {
      query: {
        node: {
          labels: ['ParkedDomain'],
          filters: [
            {
              field: 'key',
              operator: '=',
              value: parkedDomainKey || '',
            },
          ],
        },
      },
      resource: 'parkeddomain' as ResourceType,
    },
    {
      enabled: Boolean(parkedDomainKey),
      staleTime: 0, // Always fetch fresh data for drawer
    }
  );

  // Extract parked domain from response
  // Double assertion (as unknown as X) is required because useMy returns a union type
  // that TypeScript doesn't see as overlapping with ParkedDomain. The API guarantees
  // ParkedDomain shape when querying with ParkedDomain label and 'parkeddomain' resource.
  const parkedDomain = (parkedDomainData[0] as unknown as ParkedDomain) ?? null;

  return {
    parkedDomain,
    isLoading: status === 'pending',
    status,
    refetch,
  };
}
